import { atom } from "jotai";

import { isEmbedPath } from "../isEmbedPath";

import { initBridge } from "./initBridge";
import { BridgeError, type Capabilities, type LensStudioBridge } from "./types";

export interface BridgeState {
    bridge: LensStudioBridge | null;
    capabilities: Capabilities | null;
    error: BridgeError | null;
}

const NO_BRIDGE: BridgeState = { bridge: null, capabilities: null, error: null };

/**
 * Bridge mode is the default — the embed renders inside Lens Studio's QWebEngine
 * panel and the C++ side injects auth via a request interceptor. Inside `/embed/*`
 * the atom returns a Promise so embed consumers suspend until the probe completes
 * (so SSO/AuthGuard logic never runs before bridge state is known). Outside
 * `/embed/*` (main app) it returns `NO_BRIDGE` synchronously — main app has no Qt
 * host, and forcing it to suspend would gratuitously delay every consumer of
 * `useRemoteAssetsRoutes`. Jotai supports `Value | Promise<Value>` for this case.
 */
export const bridgeAtom = atom<BridgeState | Promise<BridgeState>>(() => {
    if (!isEmbedPath(window.location.pathname)) {
        return NO_BRIDGE;
    }
    return (async (): Promise<BridgeState> => {
        try {
            const bridge = await initBridge();
            if (!bridge) return NO_BRIDGE;
            const capabilities = await bridge.getCapabilities();
            return { bridge, capabilities, error: null };
        } catch (error_) {
            const error = error_ instanceof BridgeError ? error_ : new BridgeError("BRIDGE_NOT_FOUND", String(error_));
            return { bridge: null, capabilities: null, error };
        }
    })();
});
